import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import './YoutubeStoryteller.css';

const YoutubeStorytellerStep3 = ({ formData, onBack, onComplete }) => {
  const { selectedNews = [], selectedBlocks = [], selectedNarratives = [], blockNotes = '' } = formData;
  
  // Script Configuration State
  const [scriptConfig, setScriptConfig] = useState({
    videoType: 'personal-storytelling', // tutorial, personal-storytelling, vsl, ad
    videoLength: 10, // 2-30 minutes
    reelifyEnabled: false,
    captionStyles: [], // Multi-select: provocative, real, explanatory, etc.
    customTone: '', // Custom tone from creator settings
    customInstructions: blockNotes // Auto-fill with notes from Step 2
  });

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [hasCustomTone, setHasCustomTone] = useState(false);

  // State for showing style examples
  const [expandedStyle, setExpandedStyle] = useState(null);

  // Available options
  const videoTypes = [
    { value: 'personal-storytelling', label: 'Personal Storytelling', icon: '🎭', description: 'Authentic narrative-driven content you focus on storytelling and telling metaphors to make topics more relatable and engaging.' },
    { value: 'vsl', label: 'VSL (Video Sales Letter)', icon: '💰', description: 'Persuasive sales-focused video. Focus more on benefits and how it can help the viewer.' },
    { value: 'ad', label: 'Ad / Promotional', icon: '📢', description: 'Short promotional content' },
    { value: 'vlog', label: 'VLOG', icon: '🎥', description: 'Personal video blog style' },
    { value: 'learning-framework', label: 'Learning Framework', icon: '🧠', description: 'Structured educational framework. Make sure that you have an intro text and afterwards you write a framework with 3-7 steps depending on the length of the video.' },
    { value: 'surprise-me', label: 'Surprise Me', icon: '🎲', description: 'AI picks the best format' }
  ];

  const captionStyleOptions = [
    { 
      value: 'provocative', 
      label: 'Provocative', 
      icon: '🔥', 
      description: 'Bold, controversial statements',
      examples: [
        "You're doing manifestation all wrong. And it's costing you everything.",
        "The truth nobody wants to hear? Most people will fail. Here's why.",
        "This might offend you. But someone needs to say it.",
        "Stop lying to yourself. This is what actually works.",
        "They don't want you to know this. But I'm telling you anyway."
      ]
    },
    { 
      value: 'real', 
      label: 'Real Talk', 
      icon: '💯', 
      description: 'Honest, unfiltered communication',
      examples: [
        "Let me be brutally honest with you. No filter, no BS.",
        "I'm gonna tell you what nobody else will. This is the real deal.",
        "Here's the truth that most gurus won't admit.",
        "Forget what you've been told. This is what's actually happening.",
        "I tried everything. Failed miserably. Then THIS happened."
      ]
    },
    { 
      value: 'explanatory', 
      label: 'Explanatory', 
      icon: '📖', 
      description: 'Clear, educational tone',
      examples: [
        "Let me break this down for you step by step.",
        "Here's exactly how this works. Pay attention.",
        "Think of it like this. It's actually pretty simple.",
        "There are three key components to understand here.",
        "First, let's define what we're actually talking about."
      ]
    },
    { 
      value: 'motivational', 
      label: 'Motivational', 
      icon: '⚡', 
      description: 'Inspiring, action-driving',
      examples: [
        "You have everything you need right now. Let me show you.",
        "This is your moment. Don't let it slip away.",
        "Imagine waking up tomorrow and everything changed. You can.",
        "Your life is about to shift. Are you ready?",
        "Stop waiting for permission. Start NOW."
      ]
    },
    { 
      value: 'casual', 
      label: 'Casual', 
      icon: '😎', 
      description: 'Relaxed, conversational',
      examples: [
        "So here's the thing, right? It's actually pretty wild.",
        "Dude, I gotta tell you about this. It's insane.",
        "Okay so basically what happened was...",
        "Look, I'm not gonna lie. This is kinda crazy.",
        "Alright, let me paint the picture for you real quick."
      ]
    },
    { 
      value: 'professional', 
      label: 'Professional', 
      icon: '💼', 
      description: 'Business-focused, polished',
      examples: [
        "The data reveals something remarkable. Let's examine it.",
        "From a strategic perspective, this changes everything.",
        "Research indicates three critical factors at play here.",
        "Industry leaders are implementing this approach. Here's why.",
        "The competitive advantage lies in understanding this principle."
      ]
    },
    { 
      value: 'humorous', 
      label: 'Humorous', 
      icon: '😄', 
      description: 'Entertaining, funny',
      examples: [
        "So I tried manifestation. Spoiler alert: I'm still broke. Plot twist incoming.",
        "My manifestation journal? Yeah, that's a grocery list now. Whoops.",
        "They said 'just visualize success.' Cool. Still waiting. Should I call customer service?",
        "Law of attraction? More like law of 'why is nothing happening?' Am I right?",
        "Update: The universe is on vacation. Will get back to you never."
      ]
    },
    { 
      value: 'emotional', 
      label: 'Emotional', 
      icon: '❤️', 
      description: 'Deep, heartfelt connection',
      examples: [
        "I remember the exact moment everything fell apart. And it saved me.",
        "There's something I've never told anyone. Until now.",
        "That night, alone, I made a promise to myself. This is that story.",
        "When you hit rock bottom, you discover who you really are.",
        "This changed my life forever. And it will change yours too."
      ]
    }
  ];

  const totalSelectedBlocks = selectedNews.length + selectedBlocks.length + selectedNarratives.length;

  // Load creator settings on mount
  useEffect(() => {
    const loadCreatorSettings = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        
        if (!user) {
          setSettingsLoading(false);
          return;
        }

        const db = getFirestore();
        const settingsRef = doc(db, 'content', user.uid, 'creator-settings', 'profile');
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          const customTone = data.toneAndWritingStyle || '';
          
          if (customTone.trim()) {
            // Custom tone exists - set it and mark as available
            setScriptConfig(prev => ({
              ...prev,
              customTone: customTone
            }));
            setHasCustomTone(true);
            console.log('✅ [STEP3] Loaded custom tone from settings');
          }
        }
      } catch (error) {
        console.error('❌ [STEP3] Error loading creator settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadCreatorSettings();
  }, []);

  // Handlers
  const handleVideoTypeChange = (type) => {
    setScriptConfig({ ...scriptConfig, videoType: type });
  };

  const handleLengthChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setScriptConfig({ ...scriptConfig, videoLength: value });
  };

  const toggleReelify = () => {
    setScriptConfig({ ...scriptConfig, reelifyEnabled: !scriptConfig.reelifyEnabled });
  };

  const toggleCaptionStyle = (style) => {
    setScriptConfig(prev => {
      const styles = prev.captionStyles.includes(style)
        ? prev.captionStyles.filter(s => s !== style)
        : [...prev.captionStyles, style];
      return { ...prev, captionStyles: styles };
    });
  };

  const handleCustomInstructionsChange = (e) => {
    setScriptConfig({ ...scriptConfig, customInstructions: e.target.value });
  };

  const handleGenerateScript = () => {
    // Validation - at least one style OR custom tone must be present
    if (scriptConfig.captionStyles.length === 0 && !scriptConfig.customTone.trim()) {
      alert('Please select at least one Writing Style or set a Custom Tone in Settings');
      return;
    }

    // Prepare payload for AI
    const payload = {
      // From previous steps
      mainIdeas: formData.mainIdeas,
      language: formData.language,
      selectedTags: formData.selectedTags,
      
      // Selected blocks
      selectedNews,
      selectedBlocks,
      selectedNarratives,
      
      // Script configuration (includes customTone)
      scriptConfig
    };

    onComplete(payload);
  };

  const getEstimatedWordCount = () => {
    // Average speaking rate: 150 words per minute
    return scriptConfig.videoLength * 150;
  };

  const getEstimatedReelCount = () => {
    if (!scriptConfig.reelifyEnabled) return 0;
    // Rough estimate: 1 reel per 30 seconds of content
    return Math.floor(scriptConfig.videoLength * 2);
  };

  return (
    <div className="yt-step3-container">
      {/* Header */}
 

      {/* Configuration Form */}
      <div className="yt-config-form">
        
        {/* Video Type Selection */}
        <div className="yt-config-section">
          <label className="yt-config-label">
            <i className="fas fa-video yt-label-icon"></i>
            Video Type
          </label>
          <div className="yt-video-type-grid">
            {videoTypes.map(type => (
              <button
                key={type.value}
                className={`yt-video-type-card ${scriptConfig.videoType === type.value ? 'active' : ''}`}
                onClick={() => handleVideoTypeChange(type.value)}
              >
                <div className="yt-type-icon">{type.icon}</div>
                <div className="yt-type-label">{type.label}</div>
                <div className="yt-type-description">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Video Length Slider */}
        <div className="yt-config-section">
          <label className="yt-config-label">
            <i className="fas fa-clock yt-label-icon"></i>
            Video Length: <strong>{scriptConfig.videoLength} minutes</strong>
          </label>
          <div className="yt-slider-container">
            <input
              type="range"
              min="2"
              max="30"
              value={scriptConfig.videoLength}
              onChange={handleLengthChange}
              className="yt-slider"
            />
            <div className="yt-slider-labels">
              <span>2 min</span>
              <span>30 min</span>
            </div>
          </div>
          <div className="yt-length-stats">
            <div className="yt-stat">
              <i className="fas fa-file-alt yt-stat-icon"></i>
              <span className="yt-stat-text">~{getEstimatedWordCount().toLocaleString()} words</span>
            </div>
            {scriptConfig.reelifyEnabled && (
              <div className="yt-stat">
                <i className="fas fa-mobile-alt yt-stat-icon"></i>
                <span className="yt-stat-text">~{getEstimatedReelCount()} reel-ready segments</span>
              </div>
            )}
          </div>
        </div>

        {/* Reelify Toggle */}
        <div className="yt-config-section">
          <div className="yt-toggle-section">
            <div className="yt-toggle-header">
              <label className="yt-config-label">
                <i className="fas fa-mobile-alt yt-label-icon"></i>
                Reelify Mode
              </label>
              <button
                className={`yt-toggle-switch ${scriptConfig.reelifyEnabled ? 'active' : ''}`}
                onClick={toggleReelify}
              >
                <div className="yt-toggle-slider"></div>
              </button>
            </div>
            <div className="yt-toggle-description">
              {scriptConfig.reelifyEnabled ? (
                <p className="yt-reelify-active">
                  <i className="fas fa-check-circle"></i> <strong>Activated!</strong> Every paragraph will be structured as a standalone reel with its own hook and body. 
                  Your {scriptConfig.videoLength}-minute video will generate ~{getEstimatedReelCount()} reel-ready segments that work both 
                  as standalone content AND as a cohesive long-form story.
                </p>
              ) : (
                <p className="yt-reelify-inactive">
                  <i className="fas fa-info-circle"></i> Structure your script so every paragraph works as a standalone reel with strong hooks. 
                  Perfect for cutting your long-form video into multiple viral short-form clips.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Custom Tone from Settings (if available) */}
        {hasCustomTone && (
          <div className="yt-config-section">
            <label className="yt-config-label">
              <i className="fas fa-star yt-label-icon"></i>
              Your Custom Tone <span className="yt-label-hint">(Auto-loaded from Settings)</span>
            </label>
            <div className="yt-custom-tone-card">
              <div className="yt-custom-tone-header">
                <div className="yt-custom-tone-icon">
                  <i className="fas fa-magic"></i>
                </div>
                <div className="yt-custom-tone-title">
                  ✨ Custom Writing Style Active
                </div>
                <div className="yt-custom-tone-badge">
                  <i className="fas fa-check-circle"></i> Using Your Tone
                </div>
              </div>
              <div className="yt-custom-tone-content">
                {scriptConfig.customTone}
              </div>
              <div className="yt-custom-tone-footer">
                <i className="fas fa-info-circle"></i>
                This will be used as the primary writing style for your script. You can still select additional styles below to blend with your custom tone.
              </div>
            </div>
          </div>
        )}

        {/* Caption Text Style (Multi-select with Examples) */}
        <div className="yt-config-section">
          <label className="yt-config-label">
            <i className="fas fa-pen-fancy yt-label-icon"></i>
            {hasCustomTone ? 'Additional Writing Styles' : 'Writing Style'} <span className="yt-label-hint">({hasCustomTone ? 'Optional - Blend with your custom tone' : 'Select multiple - click to see examples'})</span>
          </label>
          {!hasCustomTone && (
            <p className="yt-style-hint">
              <i className="fas fa-hand-pointer"></i> Click on each style to see 5 example sentences and find your perfect tone
            </p>
          )}
          <div className="yt-caption-styles-grid">
            {captionStyleOptions.map(style => (
              <div key={style.value} className="yt-style-wrapper">
                <button
                  className={`yt-caption-style-card ${scriptConfig.captionStyles.includes(style.value) ? 'active' : ''}`}
                  onClick={() => toggleCaptionStyle(style.value)}
                >
                  <div className="yt-style-icon">{style.icon}</div>
                  <div className="yt-style-label">{style.label}</div>
                  <div className="yt-style-description">{style.description}</div>
                  <div className="yt-style-example">"{style.examples[0]}"</div>
                  {scriptConfig.captionStyles.includes(style.value) && (
                    <div className="yt-style-checkmark">✓</div>
                  )}
                </button>
              </div>
            ))}
          </div>
          {scriptConfig.captionStyles.length > 0 && (
            <div className="yt-selected-styles">
              <span className="yt-selected-label">
                <i className="fas fa-check-circle"></i> Selected styles:
              </span>
              {scriptConfig.captionStyles.map(style => {
                const styleObj = captionStyleOptions.find(s => s.value === style);
                return (
                  <span key={style} className="yt-selected-style-tag">
                    {styleObj.icon} {styleObj.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Custom Instructions */}
        <div className="yt-config-section">
          <label className="yt-config-label">
            <i className="fas fa-edit yt-label-icon"></i>
            Custom Instructions <span className="yt-label-hint">(Optional)</span>
          </label>
          <textarea
            className="yt-custom-instructions"
            placeholder="Add any specific instructions for how you want your script written... 

Examples:
- Use a specific phrase or catchphrase throughout
- Focus on a particular narrative style
- Include specific examples or references
- Tone adjustments (more serious, more energetic, etc.)"
            value={scriptConfig.customInstructions}
            onChange={handleCustomInstructionsChange}
            rows={6}
          />
          <div className="yt-char-count">
            {scriptConfig.customInstructions.length} characters
          </div>
        </div>

      </div>

      {/* Action Buttons */}
      <div className="yt-step-actions">
        <button 
          className="yt-back-icon-btn"
          onClick={onBack}
          title="Back to previous step"
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        <button 
          className="yt-btn-primary"
          onClick={handleGenerateScript}
          disabled={scriptConfig.captionStyles.length === 0 && !scriptConfig.customTone.trim()}
        >
          <i className="fas fa-robot"></i>
          Generate Script
        </button>
      </div>
    </div>
  );
};

export default YoutubeStorytellerStep3;

